import { supabase } from "../supabaseClient";
import { logger } from "../utils/logger";
import * as usersService from "./usersService";

const GROUP_COLUMNS =
  "id, name, created_at, leader_id, leader_message, wishlist_due_date, gift_exchange_date, wishlist_unlocked_override, join_code";

export async function listGroups() {
  const { data, error } = await supabase
    .from("groups")
    .select(GROUP_COLUMNS)
    .order("name", { ascending: true });

  if (error) {
    logger.error('groupsService', 'list_groups_failed', { error: error.message });
    throw error;
  }
  return data || [];
}

// Resolves a join code to its group without requiring membership — the RPC is
// SECURITY DEFINER specifically so a not-yet-a-member new user can look up a
// code they were given out-of-band. An empty result (no matching code) is not
// an error, just a miss, so it resolves to null rather than throwing.
export async function findGroupByJoinCode({ code }) {
  const { data, error } = await supabase.rpc("find_group_by_join_code", { p_code: code });

  if (error) {
    logger.error('groupsService', 'find_group_by_join_code_failed', { error: error.message });
    throw error;
  }
  return data?.[0] || null;
}

// Atomically resolves the code and adds the calling user as a member — a plain
// client-side insert into group_members has no RLS path for a non-admin (see
// the join_group_by_join_code migration for why a self-insert policy alone
// isn't safe here). The RPC derives the caller's identity server-side, so no
// userId is passed. Mirrors groupsService.addMember's first-member-leader /
// first-ever-group-wishlist / never-clobber-primary-group behavior.
export async function joinGroupByJoinCode({ code }) {
  const { data, error } = await supabase.rpc("join_group_by_join_code", { p_code: code });

  if (error) {
    logger.error('groupsService', 'join_group_by_join_code_failed', { error: error.message });
    throw error;
  }
  const group = data?.[0] || null;
  logger.info('groupsService', 'joined_group_by_code', { groupId: group?.id });
  return group;
}

// Atomically joins the group named by the caller's own JWT app_metadata.invited_group_id
// claim (set server-side only, via send-group-invites' Admin API call — never a client-
// supplied parameter, so it can't be forged). The RPC does the whole Bulk Invite
// acceptance atomically: inserts group_members, sets leader_id if first member,
// auto-creates the wishlist if it's the user's first-ever group, and guard-sets
// primary_group_id only if currently null — mirroring groupsService.addMember's rules.
// Returns void, so there's no group row to return here.
export async function acceptGroupInvite() {
  const { error } = await supabase.rpc("accept_group_invite");

  if (error) {
    logger.error('groupsService', 'accept_group_invite_failed', { error: error.message });
    throw error;
  }
  logger.info('groupsService', 'accepted_group_invite');
}

export async function getGroup({ groupId }) {
  const { data, error } = await supabase
    .from("groups")
    .select(GROUP_COLUMNS)
    .eq("id", groupId)
    .single();

  if (error) {
    logger.error('groupsService', 'get_group_failed', { groupId, error: error.message });
    throw error;
  }
  return data;
}

export async function createGroup({ name, createdBy }) {
  const { data, error } = await supabase
    .from("groups")
    .insert({ name, created_by: createdBy })
    .select(GROUP_COLUMNS)
    .single();

  if (error) {
    logger.error('groupsService', 'create_group_failed', { name, error: error.message });
    throw error;
  }
  logger.info('groupsService', 'group_created', { groupId: data.id, name });
  return data;
}

export async function renameGroup({ groupId, name }) {
  const { data, error } = await supabase
    .from("groups")
    .update({ name })
    .eq("id", groupId)
    .select(GROUP_COLUMNS)
    .single();

  if (error) {
    logger.error('groupsService', 'rename_group_failed', { groupId, name, error: error.message });
    throw error;
  }
  logger.info('groupsService', 'group_renamed', { groupId, newName: name });
  return data;
}

export async function deleteGroup({ groupId, name }) {
  const { error: clearError } = await supabase
    .from("users")
    .update({ primary_group_id: null })
    .eq("primary_group_id", groupId);

  if (clearError) {
    logger.error('groupsService', 'delete_group_failed', { groupId, error: clearError.message });
    throw clearError;
  }

  const { error } = await supabase.from("groups").delete().eq("id", groupId);

  if (error) {
    logger.error('groupsService', 'delete_group_failed', { groupId, error: error.message });
    throw error;
  }
  logger.info('groupsService', 'group_deleted', { groupId, name });
}

export async function listAllMemberships() {
  const { data, error } = await supabase
    .from("group_members")
    .select("group_id, user_id");

  if (error) {
    logger.error('groupsService', 'list_memberships_failed', { error: error.message });
    throw error;
  }
  return data || [];
}

export async function listMembershipsForUser({ userId }) {
  const { data, error } = await supabase
    .from("group_members")
    .select("group_id, groups(id, name, leader_id)")
    .eq("user_id", userId);

  if (error) {
    logger.error('groupsService', 'list_user_memberships_failed', { userId, error: error.message });
    throw error;
  }
  return data || [];
}

export async function addMember({ groupId, userId, displayName }) {
  const [groupCountRes, userCountRes] = await Promise.all([
    supabase.from("group_members").select("*", { count: "exact", head: true }).eq("group_id", groupId),
    supabase.from("group_members").select("*", { count: "exact", head: true }).eq("user_id", userId),
  ]);

  if (groupCountRes.error || userCountRes.error) {
    const error = groupCountRes.error || userCountRes.error;
    logger.error('groupsService', 'add_member_failed', { groupId, userId, error: error.message });
    throw error;
  }

  const isFirstMember = groupCountRes.count === 0;
  const isFirstEverGroup = userCountRes.count === 0;

  const { error: insertError } = await supabase
    .from("group_members")
    .insert({ group_id: groupId, user_id: userId });

  if (insertError) {
    logger.error('groupsService', 'add_member_failed', { groupId, userId, error: insertError.message });
    throw insertError;
  }
  logger.info('groupsService', 'member_added', { groupId, userId });

  // Guarded: never clobber a primary group the user already has (e.g. approving
  // a second join/create request for someone who already has one elsewhere).
  // Delegated to usersService — see docs/superpowers/specs/2026-08-04-users-migration-design.md's
  // Cross-Service Boundary section for why groupsService is allowed to import usersService here.
  await usersService.setPrimaryGroupIfUnset({ userId, groupId });

  if (isFirstMember) {
    const { error: leaderError } = await supabase
      .from("groups")
      .update({ leader_id: userId })
      .eq("id", groupId)
      .is("leader_id", null);

    if (leaderError) {
      // The membership row already landed — don't fail the join over this. A retry from
      // the caller would just hit the group_members composite PK and never recover.
      logger.error('groupsService', 'leader_assignment_failed', { groupId, userId, error: leaderError.message });
    } else {
      logger.info('groupsService', 'leader_assigned', { groupId, userId, reason: 'first_member' });
    }
  }

  if (isFirstEverGroup) {
    const { data: newList, error: listError } = await supabase
      .from("names")
      .insert({ name: displayName, user_id: userId, group_id: groupId, is_child_list: false })
      .select()
      .single();

    if (listError) {
      // Same reasoning as above: membership already succeeded, so log and move on
      // rather than throwing into an unrecoverable retry.
      logger.error('groupsService', 'wishlist_auto_create_failed', { groupId, userId, error: listError.message });
    } else {
      logger.info('groupsService', 'wishlist_auto_created', { groupId, userId, listId: newList?.id, name: displayName });
    }
  }
}

export async function removeMember({ groupId, userId }) {
  await usersService.clearPrimaryGroupIfMatches({ userId, groupId });

  const { error } = await supabase
    .from("group_members")
    .delete()
    .eq("group_id", groupId)
    .eq("user_id", userId);

  if (error) {
    logger.error('groupsService', 'remove_member_failed', { groupId, userId, error: error.message });
    throw error;
  }
  logger.info('groupsService', 'member_removed', { groupId, userId });

  const { data: leaderCleared, error: leaderError } = await supabase
    .from("groups")
    .update({ leader_id: null })
    .eq("id", groupId)
    .eq("leader_id", userId)
    .select("id");

  if (leaderError) {
    logger.error('groupsService', 'remove_member_failed', { groupId, userId, error: leaderError.message });
    throw leaderError;
  }
  if (leaderCleared?.length > 0) {
    logger.info('groupsService', 'leader_assigned', { groupId, userId: null, reason: 'leader_removed' });
  }
}

export async function setLeader({ groupId, leaderId }) {
  const { error } = await supabase
    .from("groups")
    .update({ leader_id: leaderId })
    .eq("id", groupId);

  if (error) {
    logger.error('groupsService', 'set_leader_failed', { groupId, leaderId, error: error.message });
    throw error;
  }
  logger.info('groupsService', 'leader_assigned', { groupId, userId: leaderId });
}

export async function regenerateJoinCode({ groupId }) {
  const { data: newCode, error: generateError } = await supabase.rpc("generate_group_join_code");

  if (generateError) {
    logger.error('groupsService', 'join_code_regenerate_failed', { groupId, error: generateError.message });
    throw generateError;
  }

  const { error: updateError } = await supabase
    .from("groups")
    .update({ join_code: newCode })
    .eq("id", groupId);

  if (updateError) {
    logger.error('groupsService', 'join_code_regenerate_failed', { groupId, error: updateError.message });
    throw updateError;
  }
  logger.info('groupsService', 'join_code_regenerated', { groupId });
  return newCode;
}

export async function updateLeaderMessage({ groupId, message }) {
  const { error } = await supabase
    .from("groups")
    .update({ leader_message: message || null })
    .eq("id", groupId);

  if (error) {
    logger.error('groupsService', 'leader_message_update_failed', { groupId, error: error.message });
    throw error;
  }
  logger.info('groupsService', 'leader_message_updated', { groupId });
}

export async function setWishlistDueDate({ groupId, dueDate }) {
  const { error } = await supabase
    .from("groups")
    .update({ wishlist_due_date: dueDate, wishlist_unlocked_override: false })
    .eq("id", groupId);

  if (error) {
    logger.error('groupsService', 'due_date_set_failed', { groupId, error: error.message });
    throw error;
  }
  logger.info('groupsService', 'due_date_set', { groupId, value: dueDate });
}

export async function clearWishlistDueDate({ groupId }) {
  const { error } = await supabase
    .from("groups")
    .update({ wishlist_due_date: null, wishlist_unlocked_override: false })
    .eq("id", groupId);

  if (error) {
    logger.error('groupsService', 'due_date_cleared_failed', { groupId, error: error.message });
    throw error;
  }
  logger.info('groupsService', 'due_date_cleared', { groupId });
}

export async function setGiftExchangeDate({ groupId, exchangeDate }) {
  const { error } = await supabase
    .from("groups")
    .update({ gift_exchange_date: exchangeDate })
    .eq("id", groupId);

  if (error) {
    logger.error('groupsService', 'exchange_date_set_failed', { groupId, error: error.message });
    throw error;
  }
  logger.info('groupsService', 'exchange_date_set', { groupId, value: exchangeDate });
}

export async function clearGiftExchangeDate({ groupId }) {
  const { error } = await supabase
    .from("groups")
    .update({ gift_exchange_date: null })
    .eq("id", groupId);

  if (error) {
    logger.error('groupsService', 'exchange_date_cleared_failed', { groupId, error: error.message });
    throw error;
  }
  logger.info('groupsService', 'exchange_date_cleared', { groupId });
}

export async function setWishlistUnlockOverride({ groupId, unlocked }) {
  const { error } = await supabase
    .from("groups")
    .update({ wishlist_unlocked_override: unlocked })
    .eq("id", groupId);

  if (error) {
    logger.error('groupsService', 'wishlist_unlock_override_failed', { groupId, error: error.message });
    throw error;
  }
  logger.info('groupsService', 'wishlist_unlock_override_changed', { groupId, unlocked });
}
